/**
 * Todo System Connections Component
 * Displays semantic and casual document connections plus related Basecamp todos
 * Supports promotion pattern with navigation depth (list → preview)
 */

import { useState, useEffect } from 'react';
import { FileText, Maximize2, Loader2, ListTodo } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SecondaryPanelContent, SecondaryPanelOpenButton } from '@/components/layout/secondary-panel-content';
import { MarkdownPreview } from '@/components/features/markdown-preview';
import { getDocument } from '@/lib/api';
import { useInternalMarkdownLinkClick } from '@/lib/internal-link-navigation';
import type { RelatedTodo } from '@/lib/basecamp-api';

interface Connection {
  id: string;
  title: string;
  filePath?: string;
  similarity?: number;
}

interface TodoSystemConnectionsProps {
  connections: {
    semantic: Connection[];
    casual: Connection[];
    todos?: RelatedTodo[];
  };
  onDocumentClick?: (filePath: string) => Promise<void>;
  onTodoClick?: (todoId: string) => void;
  /** Current todo's project. Related todos are the same project when the row has no project id. */
  projectId?: string;
  onDemote?: () => void;
}

function projectIdFromAppUrl(url: string | null): string | null {
  const match = url?.match(/\/(?:buckets|projects)\/(\d+)\/todos\//);
  return match?.[1] ?? null;
}

function openRelatedTodo(todo: RelatedTodo, fallbackProjectId?: string) {
  const projectId = todo.projectId || projectIdFromAppUrl(todo.appUrl) || fallbackProjectId;
  if (!projectId) return;
  window.dispatchEvent(
    new CustomEvent('integration-view-change', {
      detail: {
        tabId: `basecamp-todo-${todo.id}`,
        title: todo.title,
        type: 'integration-basecamp-todo',
        integrationData: {
          integrationId: 'basecamp',
          projectId,
          todoId: String(todo.id),
          ...(todo.projectName ? { projectName: todo.projectName } : {}),
        },
      },
    }),
  );
}

function formatDueDate(dueOn: string | null): string {
  if (!dueOn) return '';
  try {
    const d = new Date(dueOn);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dueOn;
  }
}

export function TodoSystemConnections({
  connections,
  onDocumentClick,
  onTodoClick,
  projectId,
  onDemote
}: TodoSystemConnectionsProps) {
  const [selectedDoc, setSelectedDoc] = useState<Connection | null>(null);
  const [docContent, setDocContent] = useState<string | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const onInternalLinkClick = useInternalMarkdownLinkClick(
    selectedDoc?.filePath ?? undefined
  );

  const relatedTodos = connections.todos ?? [];
  const allDocConnections = [...connections.semantic, ...connections.casual];
  const hasAny = allDocConnections.length > 0 || relatedTodos.length > 0;

  useEffect(() => {
    if (!selectedDoc) {
      setDocContent(null);
      return;
    }

    let cancelled = false;
    setIsLoadingContent(true);

    getDocument(selectedDoc.id)
      .then((doc) => {
        if (!cancelled) {
          setDocContent(doc.content || '');
        }
      })
      .catch((err) => {
        console.error('[TodoSystemConnections] Failed to load document content:', err);
        if (!cancelled) {
          setDocContent(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingContent(false);
        }
      });

    return () => { cancelled = true; };
  }, [selectedDoc?.id]);

  if (!hasAny) {
    return (
      <SecondaryPanelContent
        title="Related"
        onClose={onDemote}
      >
        <div className="p-6 text-center">
          <p className="text-sm text-muted-foreground">No related documents or todos found</p>
        </div>
      </SecondaryPanelContent>
    );
  }

  const handleDocClick = (doc: Connection) => {
    setSelectedDoc(doc);
  };

  const handleBackToList = () => {
    setSelectedDoc(null);
  };

  const handleOpenDocument = async (doc: Connection) => {
    if (onDocumentClick && doc.filePath) {
      await onDocumentClick(doc.filePath);
    }
  };

  return (
    <SecondaryPanelContent
      title={selectedDoc ? undefined : 'Related'}
      breadcrumbs={
        selectedDoc
          ? [
              { label: 'Related', onClick: handleBackToList },
              { label: selectedDoc.title },
            ]
          : undefined
      }
      onClose={selectedDoc ? handleBackToList : onDemote}
      headerActions={selectedDoc ? (
        <SecondaryPanelOpenButton
          onClick={() => handleOpenDocument(selectedDoc)}
        />
      ) : undefined}
    >
      {selectedDoc ? (
        // Preview View
        <div className="p-6">
          {isLoadingContent ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : docContent !== null ? (
            <div
              style={{
                animation: 'slideDownStagger 0.4s ease-out 0s both'
              }}
            >
              <MarkdownPreview
                content={docContent}
                className="max-w-none"
                compact
                onInternalLinkClick={onInternalLinkClick}
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              Could not load document content
            </p>
          )}
        </div>
      ) : (
        // List View
        <div className="p-4 space-y-2">
          {/* Related Basecamp Todos */}
          {relatedTodos.length > 0 && (
            <>
              {relatedTodos.map((todo, index) => (
                <div
                  key={todo.id}
                  className="p-3 rounded-lg border border-border bg-card hover:bg-muted cursor-pointer transition-colors flex items-start gap-2"
                  onClick={() => {
                    if (onTodoClick) {
                      onTodoClick(todo.id);
                      return;
                    }
                    openRelatedTodo(todo, projectId);
                  }}
                  style={{
                    animation: `slideDownStagger 0.4s ease-out ${index * 0.05}s both`
                  }}
                >
                  <ListTodo className="h-3 w-3 text-amber-500/80 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{todo.title}</p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                      <Badge
                        variant="outline"
                        className={`text-xs border ${
                          todo.status === 'completed'
                            ? 'bg-green-500/10 text-green-500 border-green-500/20'
                            : 'bg-gray-500/10 text-gray-500 border-gray-500/20'
                        }`}
                      >
                        {todo.status === 'completed' ? 'Done' : 'Active'}
                      </Badge>
                      {todo.dueOn && (
                        <span className="text-xs text-muted-foreground">{formatDueDate(todo.dueOn)}</span>
                      )}
                      {todo.projectName && (
                        <span className="text-xs text-muted-foreground truncate">{todo.projectName}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Semantic Connections */}
          {connections.semantic.length > 0 && (
            <>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1 pt-2">Related documents</p>
              {connections.semantic.map((doc, index) => (
            <div
              key={doc.id}
              className="p-3 rounded-lg border border-border bg-card hover:bg-muted cursor-pointer transition-colors group flex items-start justify-between gap-2"
              onClick={() => handleDocClick(doc)}
              style={{
                animation: `slideDownStagger 0.4s ease-out ${index * 0.05}s both`
              }}
            >
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <FileText className="h-3 w-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
                  {doc.similarity !== undefined && (
                    <Badge variant="outline" className="text-xs bg-purple-600/20 text-purple-400 mt-1">
                      {(doc.similarity * 100).toFixed(0)}% similar
                    </Badge>
                  )}
                </div>
              </div>
              <Button
                variant="secondary"
                size="icon"
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                title="Open in tab"
                onClick={async (e) => {
                  e.stopPropagation();
                  await handleOpenDocument(doc);
                }}
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
            </>
          )}

          {/* Casual References */}
          {connections.casual.length > 0 && connections.casual.map((doc, index) => (
            <div
              key={doc.id}
              className="p-3 rounded-lg border border-border bg-card hover:bg-muted cursor-pointer transition-colors group flex items-start justify-between gap-2"
              onClick={() => handleDocClick(doc)}
              style={{
                animation: `slideDownStagger 0.4s ease-out ${(connections.semantic.length + index) * 0.05}s both`
              }}
            >
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <FileText className="h-3 w-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
                  <Badge variant="outline" className="text-xs text-muted-foreground mt-1">
                    Reference
                  </Badge>
                </div>
              </div>
              <Button
                variant="secondary"
                size="icon"
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                title="Open in tab"
                onClick={async (e) => {
                  e.stopPropagation();
                  await handleOpenDocument(doc);
                }}
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </SecondaryPanelContent>
  );
}

